import React, {
  FC,
  ReactElement,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { createSelectorQuery } from "@tarojs/taro";
import { View, ScrollView } from "@tarojs/components";
import classNames from "classnames";
import Item from "./TabItem";

/**
 * Tab切换
 *
 * @param children  //子组件
 * @param animated  //是否开启转场动画
 * @param selectIndex  //默认选中第几个
 * @param lineColor   //下划线颜色
 * @param titleActiveColor   //标题选中态颜色
 * @param titleInctiveColor   //标题默认态颜色
 * @param onChange   //切换回调事件
 * @param content   //item公共节点
 * @param isOverflowNav   //是否超宽导航（超过屏幕宽度，自动滚动）
 *
 */

export interface TabProps {
  children: ReactElement[];
  animated?: boolean;
  lineColor?: string;
  titleActiveColor?: string;
  titleInctiveColor?: string;
  onChange?: (index: number) => void;
  content?: string | ReactNode;
  selectIndex: number;
  isOverflowNav?: boolean;
}

export interface TabsComponent<P = {}> extends FC<P> {
  Item: typeof Item;
}

const XHTabs: TabsComponent<TabProps> = props => {
  const {
    children = [],
    // animated = false,
    lineColor = "#FE5E00",
    titleActiveColor = "#333",
    titleInctiveColor = "#999",
    onChange,
    content,
    selectIndex = 0,
    isOverflowNav = false,
  } = props;

  const [tabContentHeight, setTabContentHeight] = useState(0);
  const [tabSlotHeight, setTabSlotHeight] = useState(0);
  const [lineLeft, setLineLeft] = useState(0); // 下划线左偏移，核心：通过状态控制下划线位置，确保同步更新和动画过渡
  const [navLeft, setNavLeft] = useState(0); // 导航滚动位置，核心：通过状态控制滚动位置，适配超宽导航
  // Ref定义
  const itemNavRef = useRef<(HTMLDivElement | null)[]>([]);
  const itemContentRef = useRef<(HTMLDivElement | null)[]>([]);
  const itemSlotRef = useRef<(HTMLDivElement | null)[]>([]);
  const navScrollRef = useRef<HTMLDivElement | null>(null); // 滚动容器ref
  const navContainerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null); // 定时器ref，避免闭包丢失

  // 统一滚动计算逻辑：H5/小程序通用
  const computeScroll = (
    itemRect: any,
    scrollRect: any,
    containerLeft: number,
    containerWidth: number
  ) => {
    if (!scrollRect || !isOverflowNav) return 0;
    // 元素在导航容器内的绝对偏移（视口坐标转容器坐标）
    const itemOffsetInNav = itemRect.left - containerLeft;
    // 居中滚动值：让选中项尽量居中显示
    let targetScroll =
      itemOffsetInNav - (scrollRect.width - itemRect.width) / 2;
    // 最大可滚动距离：总导航宽度 - 可视区域宽度
    const maxScroll = Math.max(0, containerWidth - scrollRect.width);
    // 边界处理：不小于0，不大于最大可滚动距离
    return Math.max(0, Math.min(targetScroll, maxScroll));
  };

  // 抽离位置/尺寸计算逻辑，复用（初始化+切换都调用）
  const calculateTabInfo = useCallback(
    (targetIndex: number) => {
      // 过滤空children，避免索引越界
      const validChildren = children.filter(Boolean);
      if (targetIndex < 0 || targetIndex >= validChildren.length) return;

      // H5端：直接通过ref获取元素信息，核心：同步更新，无需等待DOM渲染
      if (process.env.TARO_ENV === "h5") {
        const navItem = itemNavRef.current[targetIndex];
        const slotItem = itemSlotRef.current[0];
        const contentItem = itemContentRef.current[targetIndex];
        const navContainer = navItem?.parentElement as HTMLDivElement;
        const scrollContainer = navScrollRef.current;

        // 计算下划线位置
        if (navItem && navContainer && scrollContainer) {
          const itemRect = navItem.getBoundingClientRect();
          const containerRect = navContainer.getBoundingClientRect();
          const scrollRect = scrollContainer.getBoundingClientRect();
          const centerX =
            itemRect.left + itemRect.width / 2 - containerRect.left;
          setLineLeft(centerX);

          if (isOverflowNav) {
            const targetScroll = computeScroll(
              itemRect,
              scrollRect,
              containerRect.left,
              navContainer.offsetWidth
            );
            setNavLeft(targetScroll);
            // console.log("scrollContainer", scrollContainer.scrollTo);

            // scrollContainer.scrollTo({
            //   left: targetScroll,
            //   // behavior: "instant",
            // });
          }
        }

        if (slotItem) {
          const slotRect = slotItem.getBoundingClientRect();
          setTabSlotHeight(slotRect.height);
        }
        if (contentItem) {
          const contentRect = contentItem.getBoundingClientRect();
          setTabContentHeight(contentRect.height);
        }
        return;
      }

      // 小程序端：必须用createSelectorQuery，保留极短延迟（50ms），适配小程序DOM渲染时机
      timerRef.current = setTimeout(() => {
        const query = createSelectorQuery();
        query.selectAll(".xh-tabbar-nav-item").boundingClientRect();
        query.select(".xh-tabbar-nav").boundingClientRect(); // 导航容器（下划线定位基准）
        query.selectAll(".xh-tabbar-content-item").boundingClientRect();
        query.selectAll(".xh-tabbar-content-slot").boundingClientRect();
        query.select(".xh-tabbar-nav-scroll").boundingClientRect(); // 滚动容器
        query.exec(res => {
          const [
            navRects,
            navContainerRect,
            contentRects,
            slotRects,
            scrollRect,
          ] = res;
          const navContainer = navContainerRect?.node || navContainerRect;
          // 下划线宽度+内容高度
          if (navRects[targetIndex] && navContainer && scrollRect) {
            const itemRect = navRects[targetIndex];
            const containerRect = navContainerRect;
            const centerX =
              itemRect.left + itemRect.width / 2 - containerRect.left;
            setLineLeft(centerX);

            // 计算ScrollView滚动距离（选中项居中）
            if (isOverflowNav) {
              const targetScroll = computeScroll(
                itemRect,
                scrollRect,
                navContainerRect.left,
                navContainerRect.width
              );
              setNavLeft(targetScroll);
            }
          }
          // 内容高度计算（保持不变）
          if (contentRects[targetIndex]) {
            setTabContentHeight(contentRects[targetIndex].height);
          }
          // Slot高度查询取[0]，因为Slot只有一个节点
          if (slotRects[0]) {
            setTabSlotHeight(slotRects[0].height);
          }
        });
      }, 50);
    },
    [children, isOverflowNav]
  );

  useEffect(() => {
    calculateTabInfo(selectIndex);
    // 清理定时器，防止内存泄漏
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [selectIndex, calculateTabInfo]);

  // 点击导航项
  const handleClick = (index: number): void => {
    if (index === selectIndex) return; // 点击当前选中项，不做处理
    // 同步计算目标项位置，下划线立即更新
    calculateTabInfo(index);
    // 触发切换回调
    onChange && onChange(index);

    // 小程序端单独处理内容高度（H5已在calculateTabInfo中处理）
    if (process.env.TARO_ENV === "h5") {
      // 点击导航获取内容高度和slot高度
      const query = createSelectorQuery();
      query.selectAll(".xh-tabbar-content-item").boundingClientRect();
      query.selectAll(".xh-tabbar-content-slot").boundingClientRect();
      query.exec(res => {
        if (!res[0][index]) return;
        const height: number = res[0][index].height;

        setTabContentHeight(height);
        if (!res[1][index]) return;
        setTabSlotHeight(res[1][index].height);
      });
    }
  };

  // 下划线
  function renderLine(): ReactElement | null {
    return (
      <div
        className="xh-tabbar-nav-line"
        style={{
          background: `${lineColor}`,
          transform: `translateX(${lineLeft}px) translate(-50%, -50%)`,
          WebkitTransform: `translateX(${lineLeft}px) translate(-50%, -50%)`,
          transition: "transform 0.3s ease", // 小程序端动画过渡
        }}
      />
    );
  }

  // 导航栏
  function renderNav() {
    return (
      <ScrollView
        scrollX
        scrollLeft={navLeft}
        className="xh-tabbar-nav-scroll"
        ref={navScrollRef}
        scrollWithAnimation={isOverflowNav}
        scrollAnchoring={isOverflowNav}
      >
        <View
          className="xh-tabbar-nav"
          ref={navContainerRef}
          style={{ width: isOverflowNav ? "fit-content" : "100%" }}
        >
          {children.map((child, index) => (
            <View
              key={index}
              data-index={index}
              // @ts-ignore
              dataTrackId={
                child.props.dataTrackId || `xh-tabbar-nav-item-${index}`
              }
              ref={el => (itemNavRef.current[index] = el)}
              className={classNames("xh-tabbar-nav-item", {
                "more-item": isOverflowNav,
              })}
              style={{
                color:
                  selectIndex === index ? titleActiveColor : titleInctiveColor,
                flex: isOverflowNav ? "none" : "1",
              }}
              onClick={() => handleClick(index)}
            >
              {child.props.TabItemComponent || child.props.title}
            </View>
          ))}
          {renderLine()}
        </View>
      </ScrollView>
    );
  }

  return (
    <View className="xh-tabbar">
      {renderNav()}
      <View
        className="xh-tabbar-content"
        style={{
          width: `${children.length * 100}%`,
          height:
            tabContentHeight + tabSlotHeight
              ? `${tabContentHeight + tabSlotHeight}px`
              : "auto",
        }}
      >
        {content && (
          <View className="xh-tabbar-content-slot" ref={itemSlotRef}>
            {content}
          </View>
        )}
        {children.map((child, index) => (
          <View
            key={index}
            ref={el => (itemContentRef.current[index] = el)}
            className={classNames("xh-tabbar-content-item", {
              "xh-tabbar-content-item-dispaly": selectIndex !== index,
              "xh-tabbar-content-item-active": selectIndex === index,
            })}
          >
            {child.props.children}
          </View>
        ))}
      </View>
    </View>
  );
};

XHTabs.Item = Item;

export default XHTabs;
export const TabItem = Item;
